import * as React from 'react';
import Popover from 'src/Layer/Popover';
import SearchBox from 'src/SearchPopover/SearchBox';
import type {FocusControls} from 'src/FocusTrap';
import type {Props as SearchBoxProps} from 'src/SearchPopover/SearchBox';

export type RenderProps<RefType> = {
  anchorRef: React.RefCallback<RefType>;
  openPopover: () => void;
};

export const defaultShouldRenderCreate = (
  query: string,
  options: string[],
): boolean => {
  return (
    query.length > 0 && options.find((item) => item === query) === undefined
  );
};

type Props<RefType, Option> = SearchBoxProps<Option> & {
  onPopoverBlur?: () => void;
  renderButton: (props: RenderProps<RefType>) => React.ReactNode;
  showSearchIcon?: boolean;
};

const SearchPopover = <RefType extends HTMLElement, Option>(
  props: Props<RefType, Option>,
) => {
  const {renderButton, ...searchBoxProps} = props;
  const [open, setOpen] = React.useState(false);
  const handleClosePopover = () => setOpen(false);
  const focusControlsRef = React.useRef<FocusControls | null>(null);

  const handleSubmit = (value: Option) => {
    props.onSubmit && props.onSubmit(value);
    setTimeout(() => setOpen(false), 1);
  };

  const handleCreate = (query: string) => {
    props.creatable && props.creatable.onCreate(query);
    setTimeout(() => setOpen(false), 1);
  };

  const creatable = props.creatable
    ? {...props.creatable, onCreate: handleCreate}
    : props.creatable;

  const contents = open ? (
    <SearchBox
      {...searchBoxProps}
      creatable={creatable}
      onSubmit={handleSubmit}
      focusControlsRef={focusControlsRef}
    />
  ) : null;

  return (
    <Popover
      focusControlsRef={focusControlsRef}
      popoverContents={contents}
      popoverAnchor="top-left"
      open={open}
      onOutsideClick={handleClosePopover}
      onOutsideFocus={handleClosePopover}
    >
      {(anchorRef: React.RefCallback<RefType>) =>
        renderButton({
          anchorRef,
          openPopover: () => setOpen(true),
        })
      }
    </Popover>
  );
};

export default SearchPopover;
